import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './ui/Button';

const ROTATION_INTERVAL_MS = 4000;
const SWIPE_THRESHOLD_PX = 50;

const HeroSection: React.FC = () => {
  const { translations } = useLanguage();
  const navigate = useNavigate();

  const bannerButtons = [
    { key: 'cosmetic', label: translations.hero.banner.buttons.cosmetic },
    { key: 'pediatric', label: translations.hero.banner.buttons.pediatric },
    { key: 'general', label: translations.hero.banner.buttons.general },
    { key: 'whitening', label: translations.hero.banner.buttons.whitening },
  ];

  const handleBookClick = (treatment?: string) => {
    const bookingPath = treatment
      ? `/kontakt?booking=true&treatment=${treatment}`
      : '/kontakt?booking=true';
    navigate(bookingPath);

    const element = document.getElementById('booking-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToNextSection = () => {
    document
      .getElementById('approach')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Primary-CTA ist auf beiden Slides identisch (Buchungs-Wizard).
  // Die Slide-spezifische Aktion sitzt im unteren Mobile-CTA.
  const slides = [
    {
      key: 'primary',
      image: '/images/SeaDent Hero Smile Treatment.jpg',
      title: translations.hero.title,
      secondaryLabel: translations.hero.scrollMore,
      onSecondary: scrollToNextSection,
    },
    {
      key: 'makeover',
      image: '/images/SeaDent Hero Smile Makeover Collage.jpg',
      title: translations.hero.makeover.title,
      secondaryLabel: translations.hero.makeover.cta,
      onSecondary: () => navigate('/composite'),
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (!autoRotate) return;
    const id = window.setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, ROTATION_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [autoRotate, slides.length]);

  const goPrev = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setCurrentSlide((prev) => (prev + 1) % slides.length);

  // Touch swipe (mobile/tablet): change slide ohne Auto-Rotation zu pausieren
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > SWIPE_THRESHOLD_PX) goPrev();
    else if (dx < -SWIPE_THRESHOLD_PX) goNext();
    touchStartX.current = null;
  };

  // Desktop-Pfeil: wechselt UND pausiert die automatische Drehung
  const onArrowPrev = () => {
    setAutoRotate(false);
    goPrev();
  };
  const onArrowNext = () => {
    setAutoRotate(false);
    goNext();
  };

  return (
    <>
      <section
        className="relative min-h-[600px] lg:min-h-[700px] text-white pt-24 overflow-hidden"
        style={{ perspective: '1200px' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-roledescription="carousel"
      >
        {/* Slides */}
        <div className="absolute inset-0 z-0">
          {slides.map((slide, i) => {
            const isActive = i === currentSlide;
            return (
              <div
                key={slide.key}
                role="group"
                aria-roledescription="slide"
                aria-hidden={!isActive}
                className={`absolute inset-0 transition-all duration-700 ease-out ${
                  isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                }`}
                style={{
                  transform: isActive
                    ? 'rotateY(0deg) scale(1)'
                    : 'rotateY(40deg) scale(0.94)',
                  transformOrigin: 'center center',
                }}
              >
                {/* Background image */}
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url('${slide.image}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                  aria-hidden="true"
                ></div>

                {/* Dezenter Bottom-Gradient */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy/70 via-navy/25 to-transparent pointer-events-none"></div>

                {/* Content — nur Title + Primary-CTA, keine Subline */}
                <div className="absolute inset-0 pt-24 flex items-start">
                  <div className="container-custom relative pt-4 pb-12 w-full">
                    <div className="max-w-4xl mx-auto text-center">
                      <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-16 md:mb-24 text-white hero-title-shadow leading-tight font-title">
                        {slide.title}
                      </h1>
                      <div>
                        <Button
                          onClick={() => handleBookClick()}
                          variant="coral"
                          size="xl"
                          className="cta-mobile-wave shadow-premium hover:shadow-coral/50 transform hover:scale-105 transition-all text-lg font-bold tracking-wide"
                          rightIcon={
                            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          }
                        >
                          {translations.hero.cta}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pfeile — nur Desktop */}
        <button
          onClick={onArrowPrev}
          className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white transition-all"
          aria-label="Vorheriger Slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={onArrowNext}
          className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/20 text-white transition-all"
          aria-label="Nächster Slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Drehungs-Status — nur Desktop, klickbar zum Toggeln */}
        <button
          onClick={() => setAutoRotate((prev) => !prev)}
          className="hidden md:flex absolute bottom-20 right-6 z-20 items-center gap-1.5 text-xs text-white/85 hover:text-white bg-black/30 hover:bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full transition-all"
          aria-label={autoRotate ? 'Automatische Drehung anhalten' : 'Automatische Drehung starten'}
        >
          {autoRotate ? (
            <>
              <span className="w-1.5 h-1.5 bg-mint rounded-full animate-pulse"></span>
              {translations.hero.rotation.on}
            </>
          ) : (
            <>
              <span className="w-1.5 h-1.5 bg-coral rounded-full"></span>
              {translations.hero.rotation.off}
            </>
          )}
        </button>

        {/* Mobile Secondary CTA — Slide-spezifisch:
            Slide 1 scrollt zur Approach-Section, Slide 2 navigiert zu /composite */}
        <button
          type="button"
          onClick={slides[currentSlide].onSecondary}
          className="md:hidden absolute bottom-16 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 active:bg-white/30 backdrop-blur-md border border-white/30 text-white font-semibold text-sm px-5 py-2.5 rounded-full shadow-lg transition-all"
          aria-label={slides[currentSlide].secondaryLabel}
        >
          <span>{slides[currentSlide].secondaryLabel}</span>
          {currentSlide === 0 ? (
            <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          )}
        </button>

        {/* Pagination Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.key}
              onClick={() => setCurrentSlide(i)}
              aria-label={`Gehe zu Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
            ></button>
          ))}
        </div>
      </section>

      {/* New Modern Banner Section */}
      <section className="bg-white py-16 md:py-24 border-b border-gray-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.5" className="text-navy" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-left space-y-8">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy leading-tight font-title">
                {translations.hero.banner.title.split(',').map((part, i) => (
                  <span key={i} className="block">
                    {part.trim()}
                    {i === 0 && <span className="text-mint text-6xl">.</span>}
                  </span>
                ))}
              </h2>
              <div className="h-1 w-20 bg-mint rounded-full"></div>
              <p className="text-xl text-gray-500 font-medium italic leading-relaxed">
                {translations.hero.banner.subtitle}
              </p>
            </div>

            <div className="space-y-4">
              {bannerButtons.map((btn, index) => (
                <div
                  key={btn.key}
                  className="flex rounded-xl overflow-hidden shadow-soft hover:shadow-premium transition-all transform hover:-translate-y-1 group cursor-pointer border border-gray-100 min-h-[80px] bg-white relative"
                  onClick={() => handleBookClick(btn.key)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Left: Treatment Name */}
                  <div className="flex-grow bg-white px-5 md:px-8 py-3 flex items-center group-hover:bg-cream transition-colors">
                    <span className="text-base md:text-xl font-bold text-navy group-hover:text-navy-light transition-colors leading-tight">
                      {btn.label}
                    </span>
                  </div>

                  {/* Right: Persistently Visible Book Button
                      Mobile: Fixed width (130px) to fit "JETZT BUCHEN" / "Записаться".
                      Desktop: Larger width.
                  */}
                  <div className="bg-navy text-white flex items-center justify-center font-bold tracking-wider uppercase transition-all duration-300 w-[130px] md:w-[200px] flex-shrink-0 group-hover:bg-mint group-hover:text-navy">
                    <span className="flex items-center justify-center text-[10px] md:text-sm whitespace-nowrap px-1">
                      {translations.common.bookNow || 'Book'}
                      <svg className="w-3 h-3 md:w-4 md:h-4 ml-1.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
